import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import * as Yup from "yup";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { StyledButton } from "../../../../../shared/components/buttons/custom-button";
import FormBuilderV4 from "../../../../../shared/components/form-builder/form-builder-v4";
import {
  convertUsdaNopValueForApi,
  mapToTransactionCertificateReqDto,
  transactionCertificateReqDto,
} from "../../tc-models/tc-req-dto/transaction-certificate.req_dto";
import { dtrackItService } from "../../tc-service/dtrackit.service";
import { formatRefCodeLabel } from "../../tc-service/ref-code-label.utils";
import { standardCodeFromValue } from "./incoming-tc-details/incoming-details-apis";
import { fetchProductaddDetails } from "./certified-products/product-api";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useFormActions } from "../../../../../shared/hooks/form-builder.hooks";
import { useApi } from "../../../../../shared/hooks/useApi";
import { LoaderComponent } from "../../../../../shared/components/loaders/page-loader";
import useNotification from "../../../../../shared/hooks/useNotification";
import { InnerButton } from "../../../../../shared/components/buttons/floating-button-container";
import {
  TC_CREATE_ROUTE_CONFIG,
  TCAppConfig,
} from "../../../../../shared/app-configs/tc-app-configs";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../shared/app-configs/utilit";
import AlertPopup from "../../../../../shared/components/modals/alert-moadal";
import { RestrictedDataValidation } from "../../../../../shared/utils/custom-validation.utils";
import ActionsModalPopup from "../../../../../shared/components/modals/action-popup";
import { getTcVersionIncrementError, normalizeTcNumber } from "./tc-version.utils";
import {
  buildTcStatusOptions,
  NON_RETURN_STATUSES,
  RETURN_STATUSES,
  TcStatusOption,
} from "./tc-status-options";
import {
  AMENDMENT_REASON_OPTIONS,
  mapApiReasonOptions,
  mapCanonicalReasonOptions,
  TcReasonOption,
} from "./tc-reason-options";

const dateSchema = Yup.string()
  .required("This field is required")
  .matches(
    /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/,
    "Date must be in YYYY-MM-DD format"
  );

const TC_NUMBER_PATTERN = /^[A-Z]{3}-TC-[A-Z0-9]{10}-\d{2}$/;
const AMENDED_STATUSES = ["A", "RA"];
const INVALID_STATUSES = ["I", "RI"];

type RefCodeOption = TcReasonOption;

type ReturnTcOption = {
  tcId: string;
  tcNo: string;
  tcVersionNo: string;
  tcStatus: string;
  sourceVersion: string;
  tcStandard: string[];
};

// The returned TC's standards arrive as an array (V4) or a single string /
// comma-separated labels (V3); normalize to a plain string array.
const normalizeReturnTcStandards = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const mapRefCodeOptions = (
  items: any[] = [],
  labelBuilder: (item: any) => string = (item) =>
    formatRefCodeLabel(item.description_1, item.code_1)
): RefCodeOption[] =>
  items.map((item: any) => ({
    label: labelBuilder(item),
    value: item.code_1,
    description: String(item.description_1 ?? ""),
  }));

const getReasonDescription = (reason: string, options: RefCodeOption[], existingDescription?: string) => { 
  if (!reason) return "";
  return (
    options.find((option) => option.value === reason)?.description ??
    existingDescription ??
    ""
  );
};

// Date fields flow through the app as YYYY-MM-DD strings; tolerate the
// YYYY/MM/DD variant the form builder can emit so equality checks hold.
const normalizeDateOnly = (value?: string | null) =>
  String(value ?? "").trim().slice(0, 10).replace(/\//g, "-");

const getReturnTcRows = (payload: any): ReturnTcOption[] => {
  let rows = payload;
  if (payload?.response) {
    rows = payload.response;
  }
  if (typeof rows === "string") {
    try {
      rows = JSON.parse(rows);
    } catch {
      rows = [];
    }
  }
  return Array.isArray(rows)
    ? rows.map((item: any) => ({
        tcId: item.tcId || "",
        tcNo: item.tcNo || "",
        tcVersionNo: item.tcVersionNo || "",
        tcStatus: item.tcStatus || "",
        sourceVersion: item.sourceVersion || "",
        tcStandard: normalizeReturnTcStandards(item.tcStandard),
      }))
    : [];
};

const isFutureDate = (value?: Date | string | null) => {
  if (!value) return false;
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dateValue.setHours(0, 0, 0, 0);
  return dateValue.getTime() > today.getTime();
};

interface TransactionCertificateProps {
  onNextTab: () => Promise<void>;
}
const TransactionCertificate: React.FC<TransactionCertificateProps> = ({
  onNextTab,
}) => {
  // const getTCDetails = transactionCertificateGetService.transactionCertsGetStore((state) => state.getTCDetails);
  // const tcDetails = transactionCertificateGetService.transactionCertsGetStore((state) => state.tcDetails);
  const { TE_ID, formtype } = useParams();
  // The brand-new create page is routed as /v4/tc/create-tc/:currentTab with
  // "create-tc" as a literal segment, so `formtype` is undefined there.
  const hasCreateTc: boolean = (formtype ?? "create-tc") === "create-tc";
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";

  const { notify, showBanner } = useNotification();
  const navigate = useNavigate();
  const { getCurrentFormValues, updateField } = useFormActions(
    "Transaction-Details"
  );

  const { loading, error, executeGet, executePost } = useApi();
  const [initialValues, setInitalValues] = useState(() => ({
    ...transactionCertificateReqDto,
    tcIsReturn: transactionCertificateReqDto.tcIsReturn || "No",
  }));
  const [existingData, setExistingData] = useState(
    transactionCertificateReqDto
  );
  const [isVersionUpdated, setIsVersionUpdated] = React.useState(false);

  const [getZustandData, setZustandData] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popUpTitle, setPopUpTitle] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [popUpBtnName, setPopUpBtnName] = useState("");

  const formRef = useRef<any>(null);
  const {
    setDelayReason,
    setTCIssueDate,
    getEditTCInfo,
    setLabelGrades,
    cbLicensingCode,
    setTCStandardGOTS,
    getTCStandardGOTS,
    setEditTCInfo,
  } = useTransactionCertificateStore();
  // DAT-501 tc No rule 4: the licensing-code part (XXX) of the TC number is
  // derived from the logged-in CB (e.g. CB-CUC -> "CUC-TC-" seed).
  const cbLicensingPrefix = (cbLicensingCode || "")
    .toUpperCase()
    .replace(/^CB-/, "")
    .slice(0, 3);
  const tcNumberSeed = /^[A-Z]{3}$/.test(cbLicensingPrefix)
    ? `${cbLicensingPrefix}-TC-`
    : "";

  const [statuses, setStatuses] = useState<{
    tcStatusOptions: TcStatusOption[];
    returnTcStatusOptions: TcStatusOption[];
  }>({
    tcStatusOptions: [],
    returnTcStatusOptions: [],
  });
  const [tcStandardOptions, setTcStandardOptions] = useState<RefCodeOption[]>([]);
  const [reasonOptions, setReasonOptions] = useState({
    delayReasons: [] as RefCodeOption[],
    amendmentReasons: [] as RefCodeOption[],
    invalidationReasons: [] as RefCodeOption[],
  });
  const [returnTcModalOpen, setReturnTcModalOpen] = useState(false);
  const [returnTcLicensingOptions, setReturnTcLicensingOptions] = useState<
    RefCodeOption[]
  >([]);
  const [returnTcCbLicensingCode, setReturnTcCbLicensingCode] = useState("");
  const [returnTcSearch, setReturnTcSearch] = useState("");
  const [returnTcOptions, setReturnTcOptions] = useState<ReturnTcOption[]>([]);
  const [returnTcLoading, setReturnTcLoading] = useState(false);
  const [hasReturnTcSearched, setHasReturnTcSearched] = useState(false);
  const [returnTcVersion, setReturnTcVersion] = useState<"V3" | "V4">("V4");
  // The results list is a controlled Autocomplete popup: clicking the Search
  // button moves focus away from the input, and an uncontrolled popup would
  // never open to show the fetched options (86ajjekdn).
  const [returnTcListOpen, setReturnTcListOpen] = useState(false);
  const [selectedReturnTc, setSelectedReturnTc] =
    useState<ReturnTcOption | null>(null);
  const [earliestShipmentDate, setEarliestShipmentDate] = useState<string | null>(null);
  // Resolved description for the logged-in CB's own licensing code (from the
  // CXF ref-code list) — used to populate tcCbLicensingCodeDescription on
  // submit instead of sending it empty.
  const [cbLicensingCodeDescription, setCbLicensingCodeDescription] = useState("");

  const handlePlaceOfIssueChange = (value: any) => {
    formRef.current.setFieldValue(
      "tcPlaceOfIssue",
      value ? value.description_1 : ""
    );
  };

  const searchApi = useApi();
  const returnApi = useApi();

  const isGotsExists = async (tcStandard: string[]): Promise<boolean> => {
    return tcStandard.length === 1 && tcStandard.includes("GOTS");
  };

  const fetchPlaceDetailsFromAPI = async (query: string) => {
    try {
      const response: any = await searchApi.executeGet(
        TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4,
        {
          query_param: TCAppConfig.GET_STATE_CODE_V4,
          state_desc: query,
        }
      );

      // DTrackIt ref-code endpoints currently return either a raw array or
      // an object with the rows under `value`, depending on the caller path.
      const items = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.value)
          ? response.data.value
          : [];

      // Removed debug log for response
      return items.map((item: any) => ({
        label: `${item.description_1} (${item.code_1})`, // shown in dropdown
        value: `${item.description_1} (${item.code_1})`, // stored in form
      }));
    } catch (err) {
      console.error("Failed to fetch Place of Issue:", err);
      return [];
    }
  };

  const fetchTcBaseDetails = async () => {
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_TC_BASE_DETAILS_V4,
        {
          tcId: TE_ID,
        }
      );
      const data = mapToTransactionCertificateReqDto(response.data, TE_ID);
      data.tcCbLicensingCode = cbLicensingCode;

      await getStandardLabelGrade([...data?.tcStandard, "NA"]);
      let gotsValue = await isGotsExists(data.tcStandard);
      setTCStandardGOTS(gotsValue);
      setTCIssueDate(data?.tcIssueDate ?? "");
      setDelayReason(data?.tcDelayReason === "" ? false : true);
      setInitalValues(data);
      setExistingData(data);
      setZustandInitialState(data);
      setSelectedReturnTc(
        data.returnTcNo
          ? {
              tcId: data.returnTcId,
              tcNo: data.returnTcNo,
              tcVersionNo: data.returnTcVersionNo,
              tcStatus: "",
              sourceVersion: data.returnTcSourceVersion,
              tcStandard: [],
            }
          : null
      );
      if (data.returnTcId) {
        if (data.returnTcSourceVersion) {
          setReturnTcVersion(data.returnTcSourceVersion as "V3" | "V4");
        }
        void fetchAndCacheReturnTcProducts(data.returnTcId, data.returnTcSourceVersion);
        
        try {
          const isV3 = data.returnTcSourceVersion === "V3";
          const retResponse: any = await returnApi.executeGet(
            isV3 ? TCAppConfig.GET_TC_BASE_DETAILS : TCAppConfig.GET_TC_BASE_DETAILS_V4,
            {
              tcId: data.returnTcId,
            }
          );
          if (retResponse?.data) {
            const retSourceData = mapToTransactionCertificateReqDto(retResponse.data, data.returnTcId);
            setSelectedReturnTc(
              data.returnTcNo
                ? {
                    tcId: data.returnTcId,
                    tcNo: data.returnTcNo,
                    tcVersionNo: data.returnTcVersionNo,
                    tcStatus: "",
                    sourceVersion: data.returnTcSourceVersion,
                    tcStandard: retSourceData.tcStandard || [],
                  }
                : null
            );
          }
        } catch (err) {
          console.error("Failed to load return TC standards on mount:", err);
        }
      }

      // Fetch earliest shipment date
      try {
        const shipResponse: any = await executeGet(
          TCAppConfig.GET_SHIPMENTS_ENDPOINT_V4,
          {
            tcId: TE_ID,
          }
        );
        const dates = shipResponse?.data.response?.flatMap((shipment: any) =>
          shipment.tcShipmentInformation?.map((detail: any) => detail.tcShipmentDate) || []
        ) || [];
        if (dates.length > 0) {
          const sortedDates = dates
            .filter((d: string) => d)
            .sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime());
          setEarliestShipmentDate(sortedDates[0]);
        }
      } catch (err) {
        console.error("Failed to load shipments for delay check:", err);
      }
    } catch (err) {
      console.error("Failed to fetch TC Base Details:", err);
    }
  };

  const fetchTcBaseDetailsforSubmitFlow = async (tcid: any) => {
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_TC_BASE_DETAILS_V4,
        {
          tcId: tcid,
        }
      );
      const data = mapToTransactionCertificateReqDto(response.data, tcid);
      data.tcCbLicensingCode = cbLicensingCode;
      setTCIssueDate(data?.tcIssueDate ?? "");
      setDelayReason(data?.tcDelayReason === "" ? false : true);
      setInitalValues(data);
      setZustandInitialState(data);

      // Fetch earliest shipment date
      try {
        const shipResponse: any = await executeGet(
          TCAppConfig.GET_SHIPMENTS_ENDPOINT_V4,
          {
            tcId: tcid,
          }
        );
        const dates = shipResponse?.data.response?.flatMap((shipment: any) =>
          shipment.tcShipmentInformation?.map((detail: any) => detail.tcShipmentDate) || []
        ) || [];
        if (dates.length > 0) {
          const sortedDates = dates
            .filter((d: string) => d)
            .sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime());
          setEarliestShipmentDate(sortedDates[0]);
        }
      } catch (err) {
        console.error("Failed to load shipments for delay check:", err);
      }
    } catch (err) {
      console.error("Failed to fetch TC Base Details:", err);
    }
  };
  const setZustandInitialState = (initialState?: any) => {
    const defalutState: any = initialValues;
    //set zustand state inital values
    Object.entries(initialState ?? defalutState).forEach(([key, value]) => {
      updateField(key, value);
    });
  };

  const fetchStatuses = async () => {
    const tcStatusResponse = await dtrackItService.getTCStatus();
    setStatuses(buildTcStatusOptions(tcStatusResponse?.data));
  };

  const fetchRefCodeDropdowns = async () => {
    const results = await Promise.allSettled([
      dtrackItService.getTCStandards(),
      dtrackItService.getDelayReasons(),
      dtrackItService.getAmendmentReasons(),
      dtrackItService.getInvalidationReasons(),
      dtrackItService.getCBLicensingCodes(),
    ]);
    const rows = (index: number) =>
      results[index].status === "fulfilled"
        ? ((results[index] as PromiseFulfilledResult<any>).value?.data || [])
        : [];

    setTcStandardOptions(mapRefCodeOptions(rows(0)));
    setReasonOptions({
      delayReasons: mapApiReasonOptions(rows(1)),
      amendmentReasons: mapCanonicalReasonOptions(
        rows(2),
        AMENDMENT_REASON_OPTIONS
      ),
      invalidationReasons: mapApiReasonOptions(rows(3)),
    });
    // CB licensing codes render as the bare code (e.g. "CB-CUC") — the option
    // value stays the full ref code so the return-TC search hits the right
    // partitions (86ajjej2w).
    setReturnTcLicensingOptions(
      mapRefCodeOptions(rows(4), (item: any) => String(item?.code_1 || ""))
    );
    // Resolve the description for the logged-in CB's own licensing code so
    // submit can send tcCbLicensingCodeDescription instead of leaving it empty.
    // CXF ref-code rows aren't reliably trimmed/uppercased at the source (the
    // material-ledger CB lookup normalizes for the same reason), so compare
    // case- and whitespace-insensitively rather than with a strict === match.
    const normalizeCbCode = (value: any) => String(value || "").trim().toUpperCase();
    const targetCbCode = normalizeCbCode(cbLicensingCode);
    const matchedCbRow = rows(4).find(
      (item: any) => normalizeCbCode(item?.code_1) === targetCbCode
    );
    setCbLicensingCodeDescription(matchedCbRow?.description_1 || "");
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        const names = [
          "standards",
          "delay reasons",
          "amendment reasons",
          "invalidation reasons",
          "CB licensing codes",
        ];
        console.error(`Failed to fetch TC v4 ${names[index]}:`, result.reason);
      }
    });
  };

  const fetchReturnTcOptions = async (searchValue = returnTcSearch) => {
    if (!returnTcCbLicensingCode) {
      notify("Select a CB licensing code before searching return TCs", "warning");
      return;
    }
    const normalizedSearch = normalizeTcNumber(searchValue);
    if (!normalizedSearch) {
      notify("Enter a TC number before searching", "warning");
      return;
    }

    try {
      setReturnTcLoading(true);
      const response: any = await searchApi.executeGet(
        TCAppConfig.GET_RETURN_TC_OPTIONS_V4,
        {
          tcCbLicensingCode: returnTcCbLicensingCode,
          tcNo: normalizedSearch,
          verison: returnTcVersion,
          version: returnTcVersion,
          sourceVersion: returnTcVersion,
          limit: 50,
          offset: 0,
        }
      );
      const rows = getReturnTcRows(response?.data).filter(
        (item) => item.tcId !== TE_ID
      );
      setReturnTcOptions(rows);
      setHasReturnTcSearched(true);
      setReturnTcListOpen(true);
    } catch (err) {
      console.error("Failed to fetch return TC options:", err);
      notify("Failed to load return TCs", "error");
      setReturnTcOptions([]);
      setHasReturnTcSearched(true);
      setReturnTcListOpen(true);
    } finally {
      setReturnTcLoading(false);
    }
  };



  const clearReturnTcSelection = (setFieldValue: any) => {
    setSelectedReturnTc(null);
    setFieldValue("returnTcId", "", false);
    setFieldValue("returnTcNo", "", false);
    setFieldValue("returnTcVersionNo", "", false);
    setFieldValue("returnTcSourceVersion", "", true);
    updateField("returnTcId", "");
    updateField("returnTcNo", "");
    updateField("returnTcVersionNo", "");
    updateField("returnTcSourceVersion", "");
  };

  const fetchAndCacheReturnTcProducts = async (returnTcId: string, sourceVer?: string) => {
    try {
      const activeVersion = sourceVer || returnTcVersion;
      const isV3 = activeVersion === "V3";

      const response: any = await returnApi.executeGet(
        isV3 ? TCAppConfig.GET_PRODUCT_RAWMATERIAL_INFO_LIST : TCAppConfig.GET_LIST_PRODUCT_INFO_V4,
        {
          tcId: returnTcId,
          limit: 100,
          offset: 0,
        }
      );
      let listProducts: any[] = [];
      if (response?.data?.response) {
        if (typeof response.data.response === 'string') {
          try {
            listProducts = JSON.parse(response.data.response);
          } catch (e) {
            listProducts = [];
          }
        } else if (Array.isArray(response.data.response)) {
          listProducts = response.data.response;
        }
      }

      // Fetch component list for this return TC
      let compList: any[] = [];
      try {
        const compResponse: any = await returnApi.executeGet(
          isV3 ? TCAppConfig.GET_COMPONENT_DETAILS_LIST : TCAppConfig.GET_COMPONENT_DETAILS_LIST_V4,
          { tcId: returnTcId }
        );
        const rawCompList = compResponse?.data?.response ?? compResponse?.data;
        if (typeof rawCompList === "string") {
          compList = JSON.parse(rawCompList);
        } else if (Array.isArray(rawCompList)) {
          compList = rawCompList;
        }
      } catch (err) {
        console.error("Failed to fetch component list for return TC:", err);
      }

      // Fetch V3 global geographic split details
      let v3GeoInfo: any[] = [];
      if (isV3) {
        try {
          const geoResponse: any = await returnApi.executeGet(
            TCAppConfig.GET_CERTIFIED_WEIGHT_INFO,
            { tcId: returnTcId }
          );
          if (geoResponse?.data?.tcDetails?.tcStandardCertifiedWeightInformation) {
            v3GeoInfo = geoResponse.data.tcDetails.tcStandardCertifiedWeightInformation;
          } else if (geoResponse?.data?.tcStandardCertifiedWeightInformation) {
            v3GeoInfo = geoResponse.data.tcStandardCertifiedWeightInformation;
          }
        } catch (err) {
          console.error("Failed to fetch V3 certified weight/geographic info:", err);
        }
      }

      console.log("[fetchAndCacheReturnTcProducts] returnTcId:", returnTcId);
      console.log("[fetchAndCacheReturnTcProducts] listProducts:", listProducts);
      console.log("[fetchAndCacheReturnTcProducts] compList:", compList);

      const products = await Promise.all(
        listProducts.map(async (p: any) => {
          try {
            const detailRes = await fetchProductaddDetails(
              returnApi.executeGet,
              returnTcId,
              p.productId,
              p.shipmentId,
              isV3
            );
            const payload = detailRes?.data?.response || detailRes?.data;
            const product = payload?.tcProductInformation?.[0] || payload || p;

            // Ensure product has productId and shipmentId from list summary
            if (product) {
              product.productId = p.productId || product.productId;
              product.shipmentId = p.shipmentId || product.shipmentId;
            }

            // Map V3 global geographic split into product-level structure.
            if (isV3 && v3GeoInfo.length > 0 && product) {
              const productRmCodes = (product.tcProductRawMaterialInformation || []).map(
                (rm: any) => String(rm.tcProductRawMaterialCode || "").trim().toLowerCase()
              );
              
              const matchedGeo: any[] = [];
              v3GeoInfo.forEach((stdInfo: any) => {
                const rawMaterials = stdInfo.tcCertifiedRawMaterialInformation || [];
                rawMaterials.forEach((rm: any) => {
                  const rmCode = String(rm.tcCertifiedRawMaterialCode || "").trim().toLowerCase();
                  if (productRmCodes.includes(rmCode)) {
                    const countries = rm.tcCertifiedRawMaterialCountryOrArea || [];
                    const states = rm.tcCertifiedRawMaterialStateOrProvince || [];
                    const countryDescs = rm.tcCertifiedRawMaterialCountryOrAreaDescription || [];
                    const stateDescs = rm.tcCertifiedRawMaterialStateOrProvinceDescription || [];
                    
                    countries.forEach((country: string, idx: number) => {
                      matchedGeo.push({
                        tcProductCertifiedRawMaterialCode: rm.tcCertifiedRawMaterialCode || "",
                        tcProductCertifiedRawMaterialCodeDescription: rm.tcCertifiedRawMaterialCodeDescription || "",
                        tcProductCertifiedRawMaterialWeight: Number(rm.tcCertifiedRawMaterialWeight) || 0,
                        tcProductCertifiedRawMaterialCountryOrArea: country || "",
                        tcProductCertifiedRawMaterialCountryOrAreaDescription: countryDescs[idx] || country || "",
                        tcProductCertifiedRawMaterialStateOrProvince: states[idx] || "",
                        tcProductCertifiedRawMaterialStateOrProvinceDescription: stateDescs[idx] || states[idx] || "",
                      });
                    });
                  }
                });
              });
              
              product.tcProductCertifiedRawMaterialGeographicInformation = matchedGeo;
            }

            const pArticle = String(p.tcProductArticleNo || p.tcProductNo || p.articleNo || "").trim().toLowerCase();
            const productComps = compList.filter((c: any) => {
              const cProdId = String(c?.productId || "").trim().toLowerCase();
              const pProdId = String(p?.productId || "").trim().toLowerCase();
              if (cProdId && pProdId && cProdId === pProdId) return true;

              const cArticle = String(c?.tcAssociatedProductArticleNo || c?.tcAssociatedProductNo || c?.associatedProductNo || "").trim().toLowerCase();
              if (pArticle && cArticle && pArticle === cArticle) return true;

              return false;
            });
            console.log(`[fetchAndCacheReturnTcProducts] Product ${p.productId} comps:`, productComps);

            if (productComps.length > 0) {
              // Fetch detailed component information for each component
              const detailedComps = await Promise.all(
                productComps.map(async (c: any) => {
                  try {
                    const dResp: any = await returnApi.executeGet(
                      isV3 ? TCAppConfig.GET_COMPONENT_DETAILS : TCAppConfig.GET_COMPONENT_DETAIL_V4,
                      {
                        tcId: returnTcId,
                        productId: p.productId,
                        componentId: c.componentId,
                      }
                    );
                    const body = dResp?.data ?? dResp;
                    const stored = (body?.tcProductComponentInformation || [])[0] || c;
                    return stored;
                  } catch {
                    return c;
                  }
                })
              );
              console.log(`[fetchAndCacheReturnTcProducts] Detailed comps for ${p.productId}:`, detailedComps);
              product.tcProductComponentInformation = detailedComps;
            } else {
              product.tcProductComponentInformation = [];
            }

            return product;
          } catch (err) {
            console.error("Failed to fetch product details for return TC:", p.productId, err);
            return p;
          }
        })
      );

      console.log("[fetchAndCacheReturnTcProducts] final cached products:", products);
      useTransactionCertificateStore.getState().setReturnTcProducts?.(products);
    } catch (err) {
      console.error("Failed to fetch and cache return TC products:", err);
    }
  };

  const handleReturnTcSelect = async (option: ReturnTcOption) => {
    const activeSourceVersion = option.sourceVersion || returnTcVersion;
    setReturnTcListOpen(false);
    setSelectedReturnTc(option);
    formRef.current?.setFieldValue("returnTcId", option.tcId);
    formRef.current?.setFieldValue("returnTcNo", option.tcNo);
    formRef.current?.setFieldValue("returnTcVersionNo", option.tcVersionNo);
    formRef.current?.setFieldValue("returnTcSourceVersion", activeSourceVersion);
    updateField("returnTcId", option.tcId);
    updateField("returnTcNo", option.tcNo);
    updateField("returnTcVersionNo", option.tcVersionNo);
    updateField("returnTcSourceVersion", activeSourceVersion);
    setReturnTcModalOpen(false);
    notify("Return TC selected", "success");
    void fetchAndCacheReturnTcProducts(option.tcId, activeSourceVersion);

    try {
      const isV3 = activeSourceVersion === "V3";
      const response: any = await returnApi.executeGet(
        isV3 ? TCAppConfig.GET_TC_BASE_DETAILS : TCAppConfig.GET_TC_BASE_DETAILS_V4,
        {
          tcId: option.tcId,
        }
      );
      if (response?.data) {
        const sourceData = mapToTransactionCertificateReqDto(response.data, option.tcId);
        
        formRef.current?.setFieldValue("tcPlaceOfIssue", sourceData.tcPlaceOfIssue || "");
        formRef.current?.setFieldValue("tcIssueDate", sourceData.tcIssueDate || "");
        formRef.current?.setFieldValue("tcLastUpdatedDate", sourceData.tcLastUpdatedDate || "");
        formRef.current?.setFieldValue("tcApplicationReceivedDate", sourceData.tcApplicationReceivedDate || "");
        formRef.current?.setFieldValue("tcApplicationCompleteDate", sourceData.tcApplicationCompleteDate || "");
        formRef.current?.setFieldValue("tcDelayReason", sourceData.tcDelayReason || "");
        formRef.current?.setFieldValue("tcUsdaNopCompliantFlag", sourceData.tcUsdaNopCompliantFlag || "");
        formRef.current?.setFieldValue("tcStandard", sourceData.tcStandard || []);
        
        let newStatus = "R";
        if (sourceData.tcStatus === "A" || sourceData.tcStatus === "RA") {
          newStatus = "RA";
        }
        formRef.current?.setFieldValue("tcStatus", newStatus);

        updateField("tcPlaceOfIssue", "");
        updateField("tcIssueDate", "");
        updateField("tcLastUpdatedDate", "");
        updateField("tcApplicationReceivedDate", "");
        updateField("tcApplicationCompleteDate", "");
        updateField("tcDelayReason", "");
        updateField("tcUsdaNopCompliantFlag", sourceData.tcUsdaNopCompliantFlag || "");
        updateField("tcStandard", sourceData.tcStandard || []);
        updateField("tcStatus", newStatus);
        
        await getStandardLabelGrade([...(sourceData.tcStandard || []), "NA"]);
        let gotsValue = await isGotsExists(sourceData.tcStandard || []);
        setTCStandardGOTS(gotsValue);
        setTCIssueDate(sourceData.tcIssueDate || "");
        setDelayReason(sourceData.tcDelayReason === "" ? false : true);
      }
    } catch (err) {
      console.error("Failed to autofill base details from source TC:", err);
    }
  };

  useEffect(() => {
    var currentState: any = getCurrentFormValues();

    if (Object.keys(currentState).length === 0) {
      setZustandInitialState(null);
    } else {
      setZustandInitialState(currentState);
      setInitalValues(currentState);
    }
  }, [getZustandData]);

  const getStandardLabelGrade = async (value: any) => {
    try {
      const response: any = await returnApi.executeGet(
        TCAppConfig.GET_STANDARD_LABEL_GRADE_BY_TCSTANDARD,
        { standard: JSON.stringify(value) }
      );
      if (response.status == 200) {
        const labelGrades = response.data.map((item: any) => ({
          label: item.description_1,
          value: item.code_1,
        }));
        setLabelGrades(labelGrades);
      }
    } catch (err) {
      console.error("Failed to fetch StandardLabelGrade:", err);
    }
  };

  useEffect(() => {
    fetchStatuses();
    fetchRefCodeDropdowns();
    if (TE_ID) fetchTcBaseDetails();
  }, []);

  // Autofill the licensing-code prefix on a brand-new TC so the user only
  // types the 10-character number part (DAT-501 tc No rule 4). The version
  // number mirrors the TC number (DAT-501 tc version No rule 1).
  useEffect(() => {
    if (!hasCreateTc || TE_ID || !tcNumberSeed) return;
    const currentState: any = getCurrentFormValues() || {};
    const merged = { ...initialValues, ...currentState };
    if (normalizeTcNumber(merged.tcNo)) return; // keep user/loaded input
    setInitalValues({ ...merged, tcNo: tcNumberSeed, tcVersionNo: tcNumberSeed });
    updateField("tcNo", tcNumberSeed);
    updateField("tcVersionNo", tcNumberSeed);
  }, [tcNumberSeed, hasCreateTc, TE_ID]);

  useEffect(() => {
    if (!returnTcModalOpen) return;
    setReturnTcOptions([]);
    setHasReturnTcSearched(false);
    setReturnTcSearch("");
  }, [returnTcModalOpen]);

  // Validation function for TC Number and Version Number
  const validateTCNumberOrVersion = (value: string | undefined) => {
    if (!value) return true; // Let required validation handle empty values

    const trimmedValue = normalizeTcNumber(value);
    if (!trimmedValue) {
      return 'Cannot be blank or empty';
    }

    const disallowedValues = ['n/a', 'na', 'null', 'not applicable', 'not available'];
    const lowerValue = trimmedValue.toLowerCase();
    if (disallowedValues.includes(lowerValue)) {
      return 'Disallowed values: n/a, na, null, not applicable, not available are not permitted';
    }

    if (trimmedValue.startsWith('CB-')) {
      return 'Cannot start with "CB-" prefix';
    }

    if (!TC_NUMBER_PATTERN.test(trimmedValue)) {
      return `Expected format ${cbLicensingPrefix || "XXX"}-TC-##########-00 — exactly 10 characters (A-Z, 0-9) between "-TC-" and the 2-digit suffix`;
    }

    if (cbLicensingCode && cbLicensingCode !== `CB-${trimmedValue.slice(0, 3)}`) {
      return `Licensing code prefix must be "${cbLicensingPrefix}" to match your CB licensing code (${cbLicensingCode})`;
    }

    return true;
  };

  const transactionCertificateValidationService = {
    transactioncertsValidation: Yup.object().shape({
      tcNo: Yup.string()
        .required("Transaction Certificate Number is required")
        .test('tc-number-validation', function(value) {
          const result = validateTCNumberOrVersion(value);
          return result === true ? true : this.createError({ message: result });
        }),
      tcStandard: Yup.array()
        .min(1, "Select at least one TC Standard")
        .required("TC Standard is required")
        .test("return-tc-standards-match", function (value) {
          const { tcIsReturn } = this.parent;
          if (tcIsReturn === "Yes" && selectedReturnTc && selectedReturnTc.tcStandard && selectedReturnTc.tcStandard.length > 0) {
            const returnTcStandards = selectedReturnTc.tcStandard
              .map((std: string) => standardCodeFromValue(std).toLowerCase())
              .filter(Boolean);
            const userStds = (value || [])
              .map((std: string) => standardCodeFromValue(std).toLowerCase())
              .filter(Boolean);

            const sortedUser = [...userStds].sort();
            const sortedReturn = [...returnTcStandards].sort();
            const isSubset = userStds.every((std) => returnTcStandards.includes(std));
            if (!isSubset) {
              return this.createError({
                message: `Selected standard(s) must be a subset of the returned TC standard: ${selectedReturnTc.tcStandard.join(", ")}`,
              });
            }
          }
          return true;
        }),
      tcIssueDate: Yup.date()
        .required("TC Date of Issue is required")
        .max(new Date(), "TC Issue Date cannot be in the future")
        .test(
          "date-of-issue-validation",
          "TC cannot be issued after the TC is last updated",
          function (value) {
            const { tcLastUpdatedDate } = this.parent;
            if (!value || !tcLastUpdatedDate) return true;
            return new Date(value) <= new Date(tcLastUpdatedDate);
          }
        ),
      tcVersionNo: Yup.string()
        .required("TC Version Number is required")
        .test('tc-version-validation', function(value) {
          const result = validateTCNumberOrVersion(value);
          return result === true ? true : this.createError({ message: result });
        })
        .test("tc-version-prefix-validation", function(value) {
          const { tcNo } = this.parent;
          const normalizedTcNo = normalizeTcNumber(tcNo);
          const normalizedVersion = normalizeTcNumber(value);
          if (!normalizedTcNo || !normalizedVersion) return true;
          if (!TC_NUMBER_PATTERN.test(normalizedTcNo) || !TC_NUMBER_PATTERN.test(normalizedVersion)) return true;
          if (normalizedTcNo.slice(0, -2) !== normalizedVersion.slice(0, -2)) {
            return this.createError({
              message: "Only the last two digits are allowed to change in the version number",
            });
           }
           return true;
        })
        .test("tc-version-increment-validation", function(value) {
          const currentVersion = normalizeTcNumber(existingData?.tcVersionNo);
          const proposedVersion = normalizeTcNumber(value);
          if (!TE_ID || !currentVersion || !proposedVersion || currentVersion === proposedVersion) {
            return true;
          }
          if (!TC_NUMBER_PATTERN.test(currentVersion) || !TC_NUMBER_PATTERN.test(proposedVersion)) {
            return true;
          }

          const incrementError = getTcVersionIncrementError(currentVersion, proposedVersion);
          if (incrementError) {
            return this.createError({
              message: incrementError,
            });
          }
          return true;
        }),
      tcStatus: Yup.string()
        .required("TC Status is required")
        .test(
          "tcStatus-validation",
          "Invalid TC status",
          function (value) {
            const { tcNo, tcVersionNo, tcIsReturn } = this.parent;
            if (!value) return true;
            const isReturn = tcIsReturn === "Yes";
            if (isReturn && !RETURN_STATUSES.includes(value)) {
              return this.createError({
                message: "Return TCs allow only R, RA, or RI",
              });
            }
            if (!isReturn && !NON_RETURN_STATUSES.includes(value)) {
              return this.createError({
                message: "Non-return TCs allow only V, A, or I",
              });
            }

            const normalizedTcNo = normalizeTcNumber(tcNo);
            const normalizedVersion = normalizeTcNumber(tcVersionNo);
            const versionDiffersFromTcNo = normalizedTcNo && normalizedVersion && normalizedTcNo !== normalizedVersion;
            const versionChangedFromStored = Boolean(
              existingData?.tcVersionNo &&
              normalizeTcNumber(existingData.tcVersionNo) !== normalizedVersion
            );
            if (AMENDED_STATUSES.includes(value) && !versionDiffersFromTcNo) {
              return this.createError({
                message: "Amended statuses require the TC Version Number to differ from the TC Number",
              });
            }
            if (versionChangedFromStored && !AMENDED_STATUSES.includes(value)) {
              return this.createError({
                message: "Version number changes require Amended or Return Amended status",
              });
            }
            if (versionDiffersFromTcNo && ["V", "R"].includes(value)) {
              return this.createError({
                message: "Valid or Return status requires the TC Version Number to match the TC Number",
              });
            }
            return true; // Validation passes
          }
        ),
      tcAmendmentReason: Yup.string().nullable().test("tc-amendment-reason", function(value) {
        const { tcStatus } = this.parent;
        if (AMENDED_STATUSES.includes(tcStatus) && !value) {
          return this.createError({ message: "Amendment Reason is required" });
        }
        if (!AMENDED_STATUSES.includes(tcStatus) && value) {
          return this.createError({ message: "Amendment Reason must be blank unless status is A or RA" });
        }
        if (value && !reasonOptions.amendmentReasons.some((option) => option.value === value)) {
          return this.createError({ message: "Allowed Amendment Reason values are R, T, C, I, D, or E" });
        }
        return true;
      }),
      tcInvalidationReason: Yup.string().nullable().test("tc-invalidation-reason", function(value) {
        const { tcStatus } = this.parent;
        if (INVALID_STATUSES.includes(tcStatus) && !value) {
          return this.createError({ message: "Invalidation Reason is required" });
        }
        if (!INVALID_STATUSES.includes(tcStatus) && value) {
          return this.createError({ message: "Invalidation Reason must be blank unless status is I or RI" });
        }
        if (value && !reasonOptions.invalidationReasons.some((option) => option.value === value)) {
          return this.createError({ message: "Select a valid Invalidation Reason" });
        }
        return true;
      }),
      tcPlaceOfIssue: Yup.string()
        .required("TC Place of Issue is required")
        .max(255, "Maximum 255 Characters are Allowed"),

      tcLastUpdatedDate: Yup.date()
        .required("TC Last Updated is required")
        .max(new Date(), "TC Last Updated Date cannot be in the future")
        .test(
          "last-updated-after-issue",
          "TC Last Updated Date must be greater than or equal to TC Issue Date",
          function (value) {
            const { tcIssueDate } = this.parent;
            if (!value || !tcIssueDate) return true;
            return new Date(value) >= new Date(tcIssueDate);
          }
        )
        .test(
          "last-updated-changed-for-non-valid",
          "TC Last Updated Date must differ from TC Issue Date when TC Status is not Valid",
          function (value) {
            const { tcIssueDate, tcStatus, tcIsReturn } = this.parent;
            if (tcIsReturn === "Yes" || tcStatus === "R" || tcStatus === "RA") return true;
            if (!value || !tcIssueDate || tcStatus === "V") return true;
            return new Date(value).getTime() !== new Date(tcIssueDate).getTime();
          }
        ),

      tcApplicationReceivedDate: Yup.date().required(
        "Received Date is required"
      ),
      tcApplicationCompleteDate: Yup.date()
        .required("Completion Date is required")
        .test(
          "complete-after-received",
          "TC Application Complete Date must be greater than or equal to TC Application Received Date",
          function (value) {
            const { tcApplicationReceivedDate } = this.parent;
            if (!value || !tcApplicationReceivedDate) return true;
            return new Date(value) >= new Date(tcApplicationReceivedDate);
          }
        ),
      tcUsdaNopCompliantFlag: Yup.string()
        .nullable()
        .test(
          "UsdaNopCompliantFlag-required",
          "Certification of the organic material complies with USDA NOP rules is required when OCS is selected",
          function (value) {
            const { tcStandard } = this.parent;
            const isOCSSelected =
              Array.isArray(tcStandard) && tcStandard.includes("OCS");
            if (isOCSSelected && !value) {
              return this.createError({
                message:
                  "Certification of the organic material complies with USDA NOP rules is required",
              });
            }
            if (!isOCSSelected && value) {
              return this.createError({
                message:
                  "Certification of the organic material complies with USDA NOP rules must be blank unless OCS is selected",
              });
            }

            return true;
          }
        ),
      tcDelayReason: Yup.string()
        .required("Delay Reason is required; select NA if not applicable")
        .test('no-restricted-data', 'N/A, Not available, n/a are not allowed. Use "na" option if not applicable', 
          (value) => {
            if (!value) return true;
            const trimmedValue = value.trim();
            if (trimmedValue === "na") return true;
            return !RestrictedDataValidation.isRestrictedData(value);
          })
        .test(
          "delay-reason-required-over-180",
          "Delay Reason must be specified (cannot be 'na') when application received date is more than 180 days after the earliest shipment date",
          function (value) {
            const { tcApplicationReceivedDate } = this.parent;
            if (!tcApplicationReceivedDate || !earliestShipmentDate) return true;
            const diffTime = new Date(tcApplicationReceivedDate).getTime() - new Date(earliestShipmentDate).getTime();
            const diffDays = diffTime / (1000 * 60 * 60 * 24);
            if (diffDays > 180 && (!value || value.toLowerCase() === "na")) {
              return false;
            }
            return true;
          }
        ),
      tcIsReturn: Yup.string().required(),
      returnTcNo: Yup.string().test(
        "return-tc-required",
        "Select the Transaction Certificate being returned",
        function (value) {
          return this.parent?.tcIsReturn === "Yes" ? !!value : true;
        }
      ),
    }),
  };

  const formConfig = {
    pageName: "Transaction-Details",
    initialValues,
    validationSchema:
      transactionCertificateValidationService.transactioncertsValidation,
    sections: [
      {
        title: "Standards and Certifications",
        fields: [
          {
            label: "Select Standard",
            name: "tcStandard",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            events: {
              onChange: async (
                updatedValue: any,
                formValues: any,
                setFieldValue: (arg0: string, arg1: any) => void
              ) => {
                let gotsValue = await isGotsExists(updatedValue);
                // //alert(gotsValue)
                setTCStandardGOTS(gotsValue);
                // The USDA-NOP value is conditional data, not a hidden value
                // to carry after OCS is removed (86ajgeff9 / DAT 501).
                if (
                  !Array.isArray(updatedValue) ||
                  !updatedValue.includes("OCS")
                ) {
                  setFieldValue("tcUsdaNopCompliantFlag", "");
                }
              },
            },
            props: {
              multiple: true,
              enableSearch: true,
              options: tcStandardOptions,
              placeholder: "Select one or more options",
            },
          },
          {
            label:
              "Certification of the organic material complies with USDA NOP rules",
            name: "tcUsdaNopCompliantFlag",
            type: "radio",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              return (
                Array.isArray(value?.tcStandard) &&
                value.tcStandard.includes("OCS")
              );
            },
            readOnly: hasSubmitTc,
            visible: (value: any) =>
              Array.isArray(value?.tcStandard) &&
              value.tcStandard.includes("OCS"),
            props: {
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ],
            },
          },
        ],
      },
      {
        title: "Transaction Certificate Information",
        fields: [
          {
            label: "Transaction Certificate Number",
            name: "tcNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            // DAT-501: once a TC has an id it is being edited/amended and its
            // TC number is immutable. Do not rely on transient dashboard store
            // state, because a reload/direct URL otherwise makes it editable.
            disabled: Boolean(TE_ID),
            readOnly: hasSubmitTc || Boolean(TE_ID),
            props: {
              placeholder: "Enter Transaction Certificate Number",
              variant: "outlined",
              fullWidth: true,
              onKeyDown: (e: any) => {
                if (e.key === " ") {
                  e.preventDefault();
                }
              },
            },
            events: {
              onChange: (
                updatedValue: any,
                formValues: any,
                setFieldValue: any
              ) => {
                let normalizedValue = normalizeTcNumber(updatedValue);
                // Re-seed the autofilled licensing-code prefix when emptied
                if (!normalizedValue && tcNumberSeed) {
                  normalizedValue = tcNumberSeed;
                }

                setFieldValue("tcNo", normalizedValue);
                setFieldValue("tcVersionNo", normalizedValue);
              },
            },
          },
          {
            label: "Transaction Certificate Version Number",
            name: "tcVersionNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: {
              placeholder: "Enter Version Number",
              variant: "outlined",
              fullWidth: true,
              onKeyDown: (e: any) => {
                if (e.key === " ") {
                  e.preventDefault();
                }
              },
            },
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) => {
                const normalizedTcNo = normalizeTcNumber(formValues?.tcNo) || "";
                let normalizedValue = normalizeTcNumber(value) || "";

                if (normalizedTcNo.length > 2) {
                  const prefix = normalizedTcNo.slice(0, -2);
                  if (!normalizedValue.startsWith(prefix)) {
                    // Restore prefix if user tries to modify or delete parts of it
                    const suffix = normalizedValue.replace(prefix, "").slice(-2).replace(/[^0-9]/g, "");
                    normalizedValue = prefix + (suffix || "00");
                  } else {
                    // Allow editing only the last two digits
                    const suffix = normalizedValue.slice(prefix.length).slice(0, 2).replace(/[^0-9]/g, "");
                    normalizedValue = prefix + suffix;
                  }
                } else if (!normalizedValue) {
                  normalizedValue = normalizedTcNo || tcNumberSeed;
                }

                setFieldValue("tcVersionNo", normalizedValue);
                if (hasSubmitTc) setIsVersionUpdated(true);
              },
            },
          },
          {
            label: "Is it a Return Transaction Certificate?",
            name: "tcIsReturn",
            type: "radio",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: {
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ],
            },
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) => {
                // Reset status field when return type changes

                // Force re-render by updating the field
                setFieldValue("tcStatus", "");
                setFieldValue("tcIsReturn", value);
                if (value === "Yes") {
                  setReturnTcModalOpen(true);
                } else {
                  clearReturnTcSelection(setFieldValue);
                }
              },
            },
          },
          {
            label: "Return Transaction Certificate",
            name: "returnTcNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => value?.tcIsReturn === "Yes",
            visible: (value: any) => value?.tcIsReturn === "Yes",
            readOnly: true,
            props: {
              placeholder: "Select Return TC",
              fullWidth: true,
              InputProps: {
                endAdornment: (
                  <StyledButton
                    type="button"
                    onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                      event.preventDefault()
                    }
                    onClick={() => setReturnTcModalOpen(true)}
                    sx={{
                      minWidth: "84px",
                      marginRight: 0,
                      marginLeft: 1,
                      paddingY: 0.6,
                      paddingX: 2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedReturnTc || initialValues.returnTcNo
                      ? "Change"
                      : "Select"}
                  </StyledButton>
                ),
              },
            },
          },
          {
            label: "Status",
            name: "tcStatus",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: (formValues: any) => {
              const isReturn = formValues?.tcIsReturn === "Yes";
              return {
                options: isReturn
                  ? statuses?.returnTcStatusOptions
                  : statuses?.tcStatusOptions,
                placeholder: "Select Status",
                size: "small",
              };
            },
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) => {
                setFieldValue("tcStatus", value);
                setFieldValue("tcAmendmentReason", "");
                setFieldValue("tcInvalidationReason", "");
              },
            },
          },
          {
            label: "Delay Reason",
            name: "tcDelayReason",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            helperText: 'Please select "na" if not applicable',
            props: {
              options: reasonOptions.delayReasons,
              placeholder: "Select Delay Reason",
              size: "small",
            },
          },
          {
            label: "Amendment Reason",
            name: "tcAmendmentReason",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: (value: any) => {
              return AMENDED_STATUSES.includes(value?.tcStatus);
            },
            visible: (value: any) => AMENDED_STATUSES.includes(value?.tcStatus),
            readOnly: hasSubmitTc,
            props: {
              options: reasonOptions.amendmentReasons,
              placeholder: "Select Amendment Reason",
              size: "small",
            },
          },
          {
            label: "Invalidation Reason",
            name: "tcInvalidationReason",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            // disabled: (value: any) => value.tcStatus === 'V' || value.tcStatus === 'A',
            required: (value: any) => {
              return INVALID_STATUSES.includes(value?.tcStatus);
            },
            visible: (value: any) => INVALID_STATUSES.includes(value?.tcStatus),
            readOnly: hasSubmitTc,
            props: {
              options: reasonOptions.invalidationReasons,
              placeholder: "Select Invalidation Reason",
              size: "small",
            },
          },
        ],
      },
      {
        title: "Issuance and Application Information",
        fields: [
          {
            label: "Place of Issue",
            name: "tcPlaceOfIssue",
            type: "search",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            value: initialValues.tcPlaceOfIssue, // must match selected value
            props: {
              placeholder: "Enter Place of Issue",
              fullWidth: true,
              variant: "outlined",
              displayKey: "label", // must match property in mapped objects
              // ISO country codes such as NL are valid Place-of-Issue values.
              // The shared search control defaults to three characters, so
              // explicitly allow the two-character DAT-501 code search.
              minSearchLength: 2,
              onChange: (selected: any) => {
                // selected is the full object returned from onSearch
                const formattedValue = selected?.value ?? "";
                // Save only the value (e.g., "India (IN)")
                formRef.current.setFieldValue("tcPlaceOfIssue", formattedValue, true);
              },
              onSearch: async (query: string) => {
                // Just call fetchPlaceDetailsFromAPI and return the result
                return await fetchPlaceDetailsFromAPI(query);
              },
            },
          },
          {
            label: "TC Issue Date",
            name: "tcIssueDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc || Boolean(TE_ID),
            props: {
              placeholder: "YYYY-MM-DD",
              fullWidth: true,
            },
            events: {
              onChange: (value: any, formValues: any, setFieldValue: any) => {
                // DAT-501 tc last updated date rule 1: autofill to the TC
                // issue date but let the user change it. Keep tracking the
                // issue date until the user picks a different value.
                const previousIssueDate = normalizeDateOnly(
                  formValues?.tcIssueDate
                );
                const lastUpdatedDate = normalizeDateOnly(
                  formValues?.tcLastUpdatedDate
                );
                if (!lastUpdatedDate || lastUpdatedDate === previousIssueDate) {
                  setFieldValue("tcLastUpdatedDate", value ?? "");
                }
              },
            },
          },

          {
            label: "TC Last Updated Date",
            name: "tcLastUpdatedDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: (formValues: any) => hasSubmitTc || !formValues?.tcIssueDate,
            props: (formValues: any) => ({
              placeholder: "YYYY-MM-DD",
              fullWidth: true,
              // DAT-501: tc last updated date >= tc issue date
              minDate: formValues?.tcIssueDate
                ? moment(normalizeDateOnly(formValues.tcIssueDate))
                : undefined,
            }),
          },
          {
            label: "TC Application Received Date",
            name: "tcApplicationReceivedDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: hasSubmitTc,
            props: {
              placeholder: "YYYY-MM-DD",
              fullWidth: true,
            },
          },
          {
            label: "TC Application Complete Date",
            name: "tcApplicationCompleteDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            readOnly: (formValues: any) => hasSubmitTc || !formValues?.tcApplicationReceivedDate,
            props: (formValues: any) => ({
              placeholder: "YYYY-MM-DD",
              fullWidth: true,
              minDate: formValues?.tcApplicationReceivedDate
                ? moment(normalizeDateOnly(formValues.tcApplicationReceivedDate))
                : undefined,
            }),
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      console.log(values, 'tc form values');
      
      try {
        // Return TC: the standard being returned must exist on the returned
        // TC (86ajjekpv) — block Save & Next instead of letting an invalid
        // pairing continue to the later tabs. The returned TC's standards
        // ride on the search option rows; when they are unavailable (e.g. a
        // reloaded draft) the submit-side validation still owns the rule.
        if (String(values.tcIsReturn || "").toLowerCase().startsWith("y")) {
          const returnTcStandards = (selectedReturnTc?.tcStandard || [])
            .map((std: string) => standardCodeFromValue(std).toLowerCase())
            .filter(Boolean);
          const userStds = (values.tcStandard || [])
            .map((std: string) => standardCodeFromValue(std).toLowerCase())
            .filter(Boolean);

          if (returnTcStandards.length > 0) {
            const sortedUser = [...userStds].sort();
            const sortedReturn = [...returnTcStandards].sort();
            const isMatch = sortedUser.length === sortedReturn.length && sortedUser.every((v, i) => v === sortedReturn[i]);
            if (!isMatch) {
              notify(
                `Selected standard(s) must match the returned TC standard: ${selectedReturnTc?.tcStandard.join(", ")}`,
                "error"
              );
              return;
            }
          }
        }

        const transactionCertsData = mapToTransactionCertificateReqDto(
          {
            ...values,
            tcAmendmentReasonDescription: getReasonDescription(
              values.tcAmendmentReason,
              reasonOptions.amendmentReasons,
              values.tcAmendmentReasonDescription
            ),
            tcInvalidationReasonDescription: getReasonDescription(
              values.tcInvalidationReason,
              reasonOptions.invalidationReasons,
              values.tcInvalidationReasonDescription
            ),
            tcDelayReasonDescription: getReasonDescription(
              values.tcDelayReason,
              reasonOptions.delayReasons,
              values.tcDelayReasonDescription
            ),
          },
          TE_ID,
          isVersionUpdated
        );
        transactionCertsData.tcUsdaNopCompliantFlag = convertUsdaNopValueForApi(
          transactionCertsData.tcUsdaNopCompliantFlag
        );
        transactionCertsData.tcCbLicensingCode = cbLicensingCode;
        // Prefer the freshly resolved ref-code description; fall back to
        // whatever was already stored (e.g. a previously saved draft) so a
        // slow/failed ref-code fetch doesn't regress an existing value.
        transactionCertsData.tcCbLicensingCodeDescription =
          cbLicensingCodeDescription || transactionCertsData.tcCbLicensingCodeDescription;
        const isTcNoChanged = existingData.tcNo !== transactionCertsData.tcNo;
        const isVersionNoChanged =
          existingData.tcVersionNo !== transactionCertsData.tcVersionNo;
        const isNewlyCreated = TE_ID ? "No" : "Yes";
        const finalUrl = `${TCAppConfig.POST_TC_BASE_DETAILS_V4}?${
          TE_ID ? `tcId=${TE_ID}&` : ""
        }isNewlyCreated=${isNewlyCreated}${
          isTcNoChanged || isVersionNoChanged
            ? `&oldTcNo=${existingData.tcNo}&oldTcVersionNo=${existingData.tcVersionNo}`
            : ""
        }`;
        const result: any = await executePost(finalUrl, transactionCertsData);

        if (result?.status === 200) {
          notify("Saved Succesfully", "success");

          // Get the TC ID for navigation (use new ID if created, otherwise existing)
          const tcId = result.data?.tcId || TE_ID;

          // Ensure Zustand and local form state are in sync
          setZustandInitialState(transactionCertsData);
          setInitalValues(transactionCertsData);

          // Let parent refresh completion map
          await onNextTab();

          // If we are in submitted flow, parent may expect specific routes
          if (hasSubmitTc) {
            if (!isVersionUpdated) {
              navigate(
                `/v4/tc/tc-details/buyer-seller-products/${tcId}`
              );
            } else {
              navigate(
                `/v4/tc/tc-details/transaction-certificate/${tcId}`
              );
              if (result.data?.tcId)
                fetchTcBaseDetailsforSubmitFlow(result.data.tcId);
            }
          } else {
            // For regular create/edit flow, navigate to next tab
            navigate(
              `/v4/tc/${formtype || "create-tc"}/buyer-seller-products/${tcId}`
            );
          }
        } else {
          notify("Unable to Save", "error");
        }
        // Debug: response handle submit removed
      } catch (error) {
        console.error("Error saving Transaction Certificate:", error);
        notify("Error saving data, please try again", "error");
      }
    },
  };

  const handleSubmit = () => {
    if (hasSubmitTc) {
      if (!isVersionUpdated) {
        onNextTab?.();
      } else if (formRef.current) {
        formRef.current.submitForm();
      }
    } else if (formRef.current) {
      formRef.current.submitForm();
    }
  };
  const handleCancelAmendment = async () => {
    try {
      await executePost(TCAppConfig.POST_TC_CANCEL_AMEND_V4, {
        tcId: TE_ID,
        tcCbLicensingCode: cbLicensingCode,
      });
      // if()
      notify("Cancelled Amendment Successfully", "success");
    } catch (err) {
      console.error("Failed to cancel amendment", err);
    }
    navigate(`/v4/tc-dashboard`);
  };

  const handleCancelSave = () => {
    setDialogOpen(false);
  };

  return (
    <Box>
      {/* Render the FormBuilder component and pass formRef and onSubmit handler */}
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          <FormBuilderV4
            formConfig={formConfig}
            onSubmit={formConfig.handleSubmit}
            formRef={formRef}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              gap: "0.5rem",
            }}
          >
            {getEditTCInfo?.isParentStatus && (
              <InnerButton
                onClick={() => {
                  setDialogOpen(true);
                  setPopUpTitle("Cancel Amendment");
                  setPopUpMessage(
                    "Are you sure you want to cancel this amendment?"
                  );
                  setPopUpBtnName("Confirm");
                }}
              >
                Cancel Amendment
              </InnerButton>
            )}
            {!hasSubmitTc ? (
              <NavLink to="/v4/tc-dashboard">
                <InnerButton>Home</InnerButton>
              </NavLink>
            ) : null}
            <InnerButton onClick={handleSubmit}>
              {hasSubmitTc
                ? !isVersionUpdated
                  ? "Next"
                  : "Save"
                : "Save & Next"}
            </InnerButton>
          </Box>
          <AlertPopup
            open={dialogOpen}
            title={popUpTitle}
            message={popUpMessage}
            onClose={handleCancelSave}
            onConfirm={handleCancelAmendment}
            confirmText={popUpBtnName}
            cancelText="Cancel"
          />
          <ActionsModalPopup
            open={returnTcModalOpen}
            onClose={() => setReturnTcModalOpen(false)}
            title="Select Return Transaction Certificate"
            width="70%"
            minHeight="55%"
            maxHeight="80%"
            actions={[
              {
                label: "Close",
                onClick: () => setReturnTcModalOpen(false),
              },
            ]}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body2">
                Please add the Transaction Certificate you want to return. The selected Transaction Certificate will also be used as the incoming TC.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Source TC Version:
                </Typography>
                <RadioGroup
                  row
                  value={returnTcVersion}
                  onChange={(e) => {
                    setReturnTcVersion(e.target.value as "V3" | "V4");
                    setReturnTcOptions([]);
                    setHasReturnTcSearched(false);
                  }}
                >
                  <FormControlLabel value="V3" control={<Radio size="small" />} label="V3" />
                  <FormControlLabel value="V4" control={<Radio size="small" />} label="V4" />
                </RadioGroup>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                <Autocomplete
                  options={returnTcLicensingOptions}
                  value={
                    returnTcLicensingOptions.find(
                      (option) => option.value === returnTcCbLicensingCode
                    ) || null
                  }
                  onChange={(_event, option) => {
                    setReturnTcCbLicensingCode(option?.value || "");
                    setReturnTcSearch("");
                    setReturnTcOptions([]);
                    setHasReturnTcSearched(false);
                  }}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  sx={{ flex: 1 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Choose CB Licensing Code"
                      placeholder="Select Licensing Code"
                      required
                    />
                  )}
                />
                <Box sx={{ display: "flex", gap: 1, flex: 2 }}>
                  <Autocomplete
                    options={returnTcOptions}
                    loading={returnTcLoading}
                    inputValue={returnTcSearch}
                    open={returnTcListOpen}
                    onOpen={() => setReturnTcListOpen(true)}
                    onClose={() => setReturnTcListOpen(false)}
                    onInputChange={(_event, value, reason) => {
                      if (reason === "input" || reason === "clear") {
                        setReturnTcSearch(value);
                        setReturnTcOptions([]);
                        setHasReturnTcSearched(false);
                        setReturnTcListOpen(false);
                      }
                    }}
                    onChange={(_event, option) => {
                      if (option) handleReturnTcSelect(option);
                    }}
                    disabled={!returnTcCbLicensingCode}
                    filterOptions={(options) => options}
                    getOptionLabel={(option) => option.tcNo}
                    isOptionEqualToValue={(option, value) =>
                      option.tcId === value.tcId &&
                      option.tcVersionNo === value.tcVersionNo
                    }
                    noOptionsText={
                      hasReturnTcSearched
                        ? "No return TC options found"
                        : "Enter a TC number and search"
                    }
                    sx={{ flex: 1 }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2">{option.tcNo}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {`${option.tcVersionNo} | ${option.tcStatus} | ${option.sourceVersion}`}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Search for the TC Number"
                        placeholder="Search TC Number"
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            fetchReturnTcOptions(returnTcSearch);
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {returnTcLoading ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <StyledButton
                    type="button"
                    onClick={() => fetchReturnTcOptions(returnTcSearch)}
                    disabled={
                      returnTcLoading ||
                      !returnTcCbLicensingCode ||
                      !normalizeTcNumber(returnTcSearch)
                    }
                  >
                    Search
                  </StyledButton>
                </Box>
              </Box>
              <TextField
                size="small"
                fullWidth
                label="Selected Return Transaction Certificate"
                value={selectedReturnTc?.tcNo || initialValues.returnTcNo || ""}
                placeholder="Selected Return TC Number"
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: "#e0e0e0",
                  "& .MuiOutlinedInput-root": { backgroundColor: "#e0e0e0" },
                }}
              />
            </Box>
          </ActionsModalPopup>
        </>
      )}
    </Box>
  );
};

export default TransactionCertificate;
